import { push } from 'connected-react-router'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import { t } from 'ttag'
import api from '../../services/api.services'
import { removeCookiesAndStorage } from '../helpers/removeCookies'
import { INotification } from '../notifications/NotificationReducers'
import { MAPPED_ROUTES, ROUTES } from '../Routes/routePaths'
import {
  accountDeleted,
  clearState,
  doLogOut,
  getWhoAmI,
  getWhoAmIFail,
  getWhoAmISuccess,
  sessionExpired,
} from './authActions'
import { getCurrentRoute } from '../Routes/RouterSelector'
import { isIOS, isNative } from '../helpers/isNative'
import ApiClient from '../../services/ApiClient'
import apiUrl from '../../services/apiUrl'
import { Preferences } from '@capacitor/preferences'
import { addNotification } from '../notifications/NotificationActions'
import { Action } from 'redux-actions'
import { OnboardingStatus, ScreenOrientation, ServiceProvider, UserProfileDto } from '../../services/api.types'
import { applyScreenOrientation } from '../helpers/screenOrientation'
import { log } from '../../services/log'
import { yuMuuvPublicCompanyId } from '../../views/Settings/Settings'
import YumButton from '../../components/YumButton/YumButton'
import * as React from 'react'
import { FirebaseMessaging } from '@capacitor-firebase/messaging'
import buildVersion from '../../version.json'
import { Capacitor } from '@capacitor/core'
import { Device } from '@capacitor/device'
import Bugsnag from '@bugsnag/js'
import { askPushNotificationPermissions, setUpPushNotifications } from '../../services/pushNotifications'
import { hasFeatureFlag } from '../helpers/featureFlags'
import { getServiceProviderLabel } from '../helpers/formHelpers'
import { Badge } from '@capawesome/capacitor-badge'

const { version: appVersion } = buildVersion || { version: '' }
const deviceInfo = await Device.getInfo()
function getAppMetaData() {
  return {
    platform: Capacitor.getPlatform(),
    osVersion: deviceInfo.osVersion,
    appVersion,
  }
}
export const appMetaData = getAppMetaData()

export function* sagaWhoAmI() {
  try {
    const {
      authenticated,
      resetPasswordInProgress,
      emailVerificationInProgress = false,
      userDto = {},
      userCompanies = [],
      userCompanyRoles = [],
      userIntegrationScopes = '',
      userIntegrationProblem = false,
      session,
      pushNotifications,
      unreadNotificationsCount,
    } = yield call(api.auth.getWhoAmI, appMetaData)

    // if (isNative()) {
    // TODO: Should we set it even if it's not authenticated session?
    /*const { sessionId } = yield ApiClient.post(apiUrl('/auth/getSessionId'))
    yield Preferences.set({ key: 'sessionId', value: sessionId })*/
    // }

    if (session) {
      yield Preferences.set({ key: 'session', value: session })
    }

    const { pathname } = yield select(getCurrentRoute)
    const currentRouteIsNotPublic: boolean = MAPPED_ROUTES.some((route: any) => {
      return !route.isPublic && route.to === pathname
    })

    if (!authenticated && currentRouteIsNotPublic) {
      console.log('SESSION EXPIRED')
      yield put({ type: String(sessionExpired) })
      /*Bugsnag.notify(new Error('Session expired'), function (event) {
        event.context = 'sagaWhoAmI'
      })*/
    }

    // Zombie session may exist for a deleted user.
    if (authenticated && !userDto && currentRouteIsNotPublic) {
      console.log('ACCOUNT DELETED')
      yield put({ type: String(accountDeleted) })
      /*Bugsnag.notify(new Error('Accessing deleted account'), function (event) {
        event.context = 'sagaWhoAmI'
      })*/
    }

    yield put({
      type: String(getWhoAmISuccess),
      payload: {
        authenticated,
        userDto,
        resetPasswordInProgress,
        emailVerificationInProgress,
        userCompanies,
        userCompanyRoles,
        userIntegrationScopes,
        userIntegrationProblem,
        pushNotifications,
        unreadNotificationsCount,
      },
    })

    if (authenticated && userDto) {
      const userOrientation = userDto.screenOrientation ?? ScreenOrientation.AUTO
      yield call(applyScreenOrientation, userOrientation)
      // Mirror the server-side preference into local Preferences so that
      // anonymous flows after logout pick up the same orientation.
      yield Preferences.set({ key: 'yumA11yOrientation', value: userOrientation })
    }

    const isLeaderboard = pathname.startsWith('/leaderboard/')
    const isVerifyEmail = pathname === ROUTES.VERIFY_EMAIL.to

    if (authenticated && !isLeaderboard && !isVerifyEmail) {
      document.body.classList.add('logged-in')
    } else {
      document.body.classList.remove('logged-in')
    }
  } catch (e: any) {
    console.error('WhoAmIFailed: ', e)
    yield put({ type: String(getWhoAmIFail) })
  }
}

export function* sagaLogOut({ payload }: Action<any>) {
  try {
    if (isNative()) {
      console.log(`Unregistering push notifications...`)
      const { value: deviceToken } = yield Preferences.get({ key: 'currentPushToken' })

      try {
        // Unregister push notifications.
        yield call(api.users.removeUserDevice, { token: deviceToken })
      } catch (e: any) {
        console.error('removeUserDevice failed (sagaLogOut): ', e)
      }

      if (deviceToken) {
        yield Preferences.remove({ key: 'currentPushToken' })
      }

      try {
        yield FirebaseMessaging.deleteToken()
      } catch (e: any) {
        console.error('FirebaseMessaging.deleteToken() failed: ', e)
      }
    }

    // console.log('Clearing state')
    yield put({ type: String(clearState) })
    document.body.classList.remove('logged-in')
    yield call(api.auth.logout)
    yield removeCookiesAndStorage()
    if (!payload || !payload.skipRedirect) {
      yield put(push(ROUTES.LANDING.to))
    }
    // To prevent having no session when trying to log in again.
    yield put({ type: String(getWhoAmI) })
  } catch (e: any) {
    console.error('LogOutFailed (sagaLogOut): ', e)
  }
}

export function* sagaSessionExpired() {
  try {
    yield put({ type: String(doLogOut), payload: { skipRedirect: true } })
    const notification: INotification = {
      type: 'warning',
      content: t`Session expired`,
    }
    yield put(push(ROUTES.LOGIN.to, { notification }))
  } catch (e: any) {
    console.error('LogOutFailed (sagaSessionExpired): ', e)
  }
}

export function* sagaAccountDeleted() {
  try {
    yield put({ type: String(doLogOut), payload: { skipRedirect: true } })
    const notification: INotification = {
      type: 'warning',
      content: t`This account has been deleted. Please contact your admin for more details.`,
    }
    yield put(push(ROUTES.LOGIN.to, { notification }))
  } catch (e: any) {
    console.error('LogOutFailed (sagaAccountDeleted): ', e)
  }
}

export function* sagaWhoAmISuccess({ payload }: Action<any>) {
  const { authenticated, userCompanies, userDto, userIntegrationProblem, pushNotifications, unreadNotificationsCount } =
    payload
  if (authenticated && userDto) {
    // Check notification preferences
    if (isNative() && userDto.onboardingStatus === 'DONE' && pushNotifications) {
      try {
        yield askPushNotificationPermissions()
        yield setUpPushNotifications()
      } catch (e: any) {
        console.error('setUpPushNotifications failed: ', e)
      }

      // This alone would trigger notification prompt!
      // Android badge support is not good unfortunately...
      if (isIOS) {
        try {
          console.log('Set badge to', userDto.unreadNotificationsCount)
          if (userDto.unreadNotificationsCount === 0) {
            yield Badge.clear()
          } else if (userDto.unreadNotificationsCount > 0) {
            yield Badge.set({ count: userDto.unreadNotificationsCount })
          }
        } catch (e: any) {
          console.error('set badge failed: ', e)
        }
      }
    }

    if (userDto.serviceProvider) {
      let serviceProvider: ServiceProvider | string = userDto.serviceProvider
      const scope = userDto.dateOfBirth ? 'FULL' : 'LEAN'
      /*const errorString = JSON.stringify(userDto.integrationError)
      const googleFitKnownError = errorString.includes('Forbidden') || errorString.includes('invalid_grant')
      const fitbitKnownError =
        errorString.includes('Forbidden') ||
        errorString.includes('invalid_grant') ||
        errorString.includes('Unauthorized')*/
      const integrationDeprecated =
        (serviceProvider === ServiceProvider.FITBIT && hasFeatureFlag(userCompanies, 'fitbitDeprecationNotice')) ||
        (serviceProvider === ServiceProvider.GOOGLE_FIT && hasFeatureFlag(userCompanies, 'googleFitDeprecationNotice'))
      if (integrationDeprecated) {
        // Takes priority over the reauthorize warning below — re-authorizing a deprecated API is pointless.
        const serviceProviderLabel = getServiceProviderLabel(userDto.serviceProvider)
        const msg = t`The ${serviceProviderLabel} integration is deprecated by Google and will stop working soon. Please choose a new integration, for example Google Health.`
        const notification: INotification = {
          type: 'warning',
          content: (
            <div>
              <span>{msg}</span>{' '}
              <YumButton
                size="small"
                style={{
                  marginTop: '10px',
                  verticalAlign: 'baseline',
                }}
                to={ROUTES.CHANGE_INTEGRATION.to}
              >{t`Choose new integration`}</YumButton>
            </div>
          ),
        }
        yield put({ type: String(addNotification), payload: notification })
      } else if (
        userIntegrationProblem
        //serviceProvider === ServiceProvider.HUAWEI ||
        // || (serviceProvider === ServiceProvider.GOOGLE_FIT && googleFitKnownError)
        // (serviceProvider === ServiceProvider.FITBIT && fitbitKnownError)
      ) {
        //const msg = t`There seems to be a problem with your ${serviceProvider} integration.`
        const integrationUrl = `${ROUTES.INTEGRATION_PROXY.to}?type=${serviceProvider}&scope=${scope}`
        serviceProvider = serviceProvider === ServiceProvider.HUAWEI ? 'HUAWEI Health Kit' : serviceProvider
        const msg = t`We seem to have insufficient permissions to sync with your ${serviceProvider} integration.`
        const notification: INotification = {
          type: 'warning',
          content: (
            <div>
              <span>{msg}</span>{' '}
              <YumButton
                size="small"
                style={{
                  marginTop: '10px',
                  verticalAlign: 'baseline',
                }}
                to={integrationUrl}
              >{t`Reauthorize`}</YumButton>
            </div>
          ),
        }
        yield put({ type: String(addNotification), payload: notification })
      }
    }

    const companies = userCompanies.filter((uc: { companyId: string }) => uc.companyId !== yuMuuvPublicCompanyId)
    if (companies.length) {
      const { onboardingStatus } = userDto
      // Check if all user related company relations have been disabled.
      const deactivated = companies.every((uc: { active: boolean }) => !uc.active)
      if (deactivated && onboardingStatus === OnboardingStatus.DONE) {
        const notification: INotification = {
          type: 'warning',
          content: t`Your account has been deactivated. Please contact your admin for more details.`,
        }
        yield put({ type: String(addNotification), payload: notification })
      }
    }
  }

  /*// Redirect back to last active page after CodePush update.
  const lastLocation = window.localStorage.getItem('lastLocationBeforeCodePush')
  if (lastLocation) {
    log(`Redirect after update to: ${lastLocation}`)
    window.localStorage.removeItem('lastLocationBeforeCodePush')
    yield put(push(lastLocation))
  }*/
}

export function* authSagas() {
  yield all([
    takeLatest(String(getWhoAmI), sagaWhoAmI),
    takeLatest(String(doLogOut), sagaLogOut),
    takeLatest(String(sessionExpired), sagaSessionExpired),
    takeLatest(String(accountDeleted), sagaAccountDeleted),
    takeLatest(String(getWhoAmISuccess), sagaWhoAmISuccess),
  ])
}
